import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { JOURNAL_ENTRIES, LIBRARY_IMAGE, DARRYL_PHOTO } from "@/lib/content";
import SEO from "@/components/SEO";
import { DashTitle } from "@/components/DashTitle";
import StateOfFirmSubscribe from "@/components/StateOfFirmSubscribe";
import { breadcrumbSchema } from "@/lib/schemas";

// Unpublished essay drafts — metadata retained for future publication.
// Kept out of ALL so they do NOT render as clickable public links until
// their corresponding article routes actually exist.
const EXTRA = [];

const ALL = [...JOURNAL_ENTRIES];

export default function Journal() {
  const [email, setEmail] = useState("");
  const [subState, setSubState] = useState("idle"); // idle | sending | done

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubState("sending");
    try {
      const { data } = await api.post("/subscribe", { email: email.trim(), source: "journal" });
      setSubState("done");
      toast.success(data.already_subscribed ? "You're already on the list." : "Check your inbox — one click confirms.");
      setEmail("");
      setTimeout(() => setSubState("idle"), 6000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe");
      setSubState("idle");
    }
  };

  return (
    <div data-testid="journal-page" className="bg-ivory">
      <SEO
        title="The Journal — Letters from Lone Star Counsel"
        description="Considered correspondence on Texas business law, asset protection, estate planning, and the affairs of closely held capital. Published quarterly."
        path="/journal"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Journal", path: "/journal" },
        ])}
      />
      {/* Hero */}
      <section className="panel-navy py-32 md:py-40 grain grain-dark relative overflow-hidden" data-testid="journal-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="" className="w-full h-full object-cover opacity-25" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy"></div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker kicker-ivory">The Journal</div>
          <h1 className="mt-6 font-display text-ivory text-6xl md:text-8xl leading-[0.95]">
            Notes from <br/>
            <span className="italic font-cormorant text-brass">the firm.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-ivory/75 text-lg font-light leading-relaxed">
            Weekly blogs and essays on Texas business law, business succession planning,
            buy-sell agreements, asset purchase agreements, mergers and acquisitions,
            trusts, estate planning, family wealth, probate and restructuring.
            Plain-spoken. No filler.
          </p>
        </div>
      </section>

      {/* Feature */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <article className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b-0" data-testid="journal-featured-article">
          <div className="lg:col-span-6">
            {ALL[0].coverSquare ? (
              <div className="aspect-square w-full max-w-[640px] mx-auto bg-[#031E3D]" data-testid="journal-featured-image">
                <img
                  src={ALL[0].cover}
                  srcSet={`${ALL[0].coverSmall} 720w, ${ALL[0].cover} 1254w`}
                  sizes="(min-width: 1024px) 640px, 100vw"
                  width="1254"
                  height="1254"
                  alt={ALL[0].coverAlt || ALL[0].title}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
            <div className="image-frame aspect-[4/3]">
              <img src={ALL[0].cover || LIBRARY_IMAGE} alt={ALL[0].title} />
            </div>
            )}
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.24em] text-slate2">
              <span>{ALL[0].category} · Featured</span>
              <span>{ALL[0].date} · {ALL[0].minutes} min read</span>
            </div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.0]"><DashTitle text={ALL[0].title} /></h2>
            {ALL[0].preview ? (
              <div className="mt-8 space-y-5" data-testid="journal-featured-preview">
                {ALL[0].preview.map((t, i) => (
                  <p key={i} className={i === 0 ? "text-xl text-navy font-cormorant italic leading-relaxed" : "text-lg text-slate2 font-light leading-relaxed"}>{t}</p>
                ))}
              </div>
            ) : (
            <p className="mt-8 text-xl font-light text-slate2 leading-relaxed font-cormorant">{ALL[0].excerpt}</p>
            )}
            <div className="mt-8 flex items-center gap-3">
              <img
                src={DARRYL_PHOTO}
                alt="Darryl V. Pratt"
                className="w-11 h-11 rounded-full object-cover object-top grayscale-[10%]"
                loading="lazy"
              />
              <div className="leading-tight">
                <div className="text-[11px] uppercase tracking-[0.24em] text-navy font-semibold">
                  {ALL[0].author}
                </div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-brass">
                  {ALL[0].preview ? "Nearly 30 Years of Experience | Licensed in Texas & Oklahoma" : "Managing Partner"}
                </div>
              </div>
            </div>
            {ALL[0].preview && (
              <div className="mt-6 space-y-2" data-testid="journal-featured-contact">
                <div className="font-serif text-lg text-navy">
                  <a href="tel:+19727121515" className="hover:text-oxblood" data-testid="journal-featured-phone-0">(972) 712-1515</a>
                  <span className="mx-3 text-brass">|</span>
                  <a href="tel:+18885174575" className="hover:text-oxblood" data-testid="journal-featured-phone-1">(888) 517-4575</a>
                </div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-brass">Planning Today. Protecting Tomorrow.&reg;</div>
              </div>
            )}
            <div className="mt-10">
              <Link to={`/journal/${ALL[0].slug}`} className="btn-primary" data-testid="journal-read-feature">{ALL[0].previewCta || "Read the essay →"}</Link>
            </div>
          </div>
        </article>

        {/* List */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 md:divide-x divide-brass/30 border-y border-transparent">
          {ALL.slice(1).map((j, i) => (
            <Link key={i} to={`/journal/${j.slug}`} className="group py-10 md:px-10 md:py-12 first:md:pl-0 last:md:pr-0 border-b md:border-b-0 border-transparent last:border-b-0" data-testid={`journal-article-${i}`}>
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.24em] text-slate2">
                <span>{j.category}</span>
                <span>{j.date} · {j.minutes} min</span>
              </div>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl leading-[1.05] group-hover:text-oxblood transition-colors">
                {j.title}
              </h3>
              <p className="mt-5 text-slate2 font-light leading-relaxed">{j.excerpt}</p>
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={DARRYL_PHOTO}
                    alt={j.author}
                    className="w-8 h-8 rounded-full object-cover object-top grayscale-[10%]"
                    loading="lazy"
                  />
                  <div className="leading-tight">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-navy font-semibold">
                      {j.author}
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.24em] text-brass">
                      Managing Partner
                    </div>
                  </div>
                </div>
                <span className="text-xs uppercase tracking-[0.24em] text-oxblood opacity-0 group-hover:opacity-100 transition">Read →</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Subscribe block — borderless, atmospheric */}
        <div className="mt-32 panel-navy grain grain-dark py-20 md:py-28 -mx-6 md:-mx-12 px-6 md:px-12" data-testid="journal-subscribe">          <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="kicker kicker-ivory">Subscribe</div>
              <h3 className="mt-4 font-display text-3xl md:text-5xl leading-[1.05] text-ivory">
                The Journal, by post or by inbox.<br/>
                <span className="italic font-cormorant text-brass">Quarterly. Never sold.</span>
              </h3>
              <p className="mt-6 text-ivory/70 font-light max-w-xl">
                Each volume contains three to four plain-spoken essays from the firm. No promotional
                copy, no upselling. Just the work.
              </p>
            </div>
            <form className="lg:col-span-5 flex flex-col gap-4" onSubmit={subscribe} data-testid="journal-subscribe-form">
              <input
                type="email"
                required
                placeholder="your.email@firm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field !bg-transparent !text-ivory !border-ivory/30 placeholder:text-ivory/40 focus:!border-brass"
                data-testid="journal-subscribe-email"
                disabled={subState === "sending"}
              />
              <button
                type="submit"
                className="btn-brass self-start disabled:opacity-40"
                data-testid="journal-subscribe-submit"
                disabled={subState === "sending"}
              >
                {subState === "sending" ? "Subscribing…" : subState === "done" ? "✓ Subscribed" : "Subscribe to the Journal"}
              </button>
              <div className="text-xs uppercase tracking-[0.2em] text-ivory/40">
                We mail twice a year. You may unsubscribe by reply.
              </div>
            </form>
          </div>
        </div>

        {/* State of the Firm — secondary opt-in for the quarterly letter */}
        <div className="mt-4 -mx-6 md:-mx-12">
          <StateOfFirmSubscribe variant="dark" />
        </div>
      </section>
    </div>
  );
}
